import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import {
  ReactFlow,
  Node,
  Edge,
  Background,
  Panel,
  MarkerType,
  Handle,
  Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { DependencyNodeId } from 'sched';
import { useDatabase } from '../../hooks/useDatabase';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useNavigate } from 'react-router-dom';
import ELK from 'elkjs/lib/elk.bundled.js';
import type { NodeTypes } from '@xyflow/react';
import { FiCheckSquare, FiFlag, FiGitBranch, FiLayers } from 'react-icons/fi';
import { Tooltip } from 'flowbite-react';

// Helper function to convert DependencyNodeId to string
export const dependencyNodeIdToString = (nodeId: DependencyNodeId): string => {
  if ('Task' in nodeId) {
    return `Task(${nodeId.Task})`;
  } else if ('Outcome' in nodeId) {
    return `Outcome(${nodeId.Outcome})`;
  } else if ('Decision' in nodeId) {
    return `Decision(${nodeId.Decision})`;
  } else if ('WorkflowInstance' in nodeId) {
    return `WorkflowInstance(${nodeId.WorkflowInstance})`;
  }
  throw new Error('Unknown node ID type');
};

// Initialize ELK layout engine
const elk = new ELK();

// ELK layout options
const elkOptions = {
  'elk.algorithm': 'layered',
  'elk.direction': 'DOWN',
  'elk.spacing.nodeNode': '50',
  'elk.layered.spacing.nodeNodeBetweenLayers': '50'
};

interface DependencyGraphProps {
  nodeIds: DependencyNodeId[];
  className?: string;
  maxNodes?: number;
}

// Styled dependency node (Task/Outcome) — reuses visual ideas from WorkflowGraph
type DependencyNodeData = {
  id: string;
  kind: 'task' | 'outcome' | 'decision' | 'workflow_instance';
  title: string;
  complete: boolean;
  meta?: string | null;
  linkHref?: string;
};

const kindStyle: Record<string, { Icon: typeof FiCheckSquare; bg: string; text: string }> = {
  task:              { Icon: FiCheckSquare, bg: 'bg-blue-50 dark:bg-blue-900/40',   text: 'text-blue-600' },
  outcome:           { Icon: FiFlag,        bg: 'bg-amber-50 dark:bg-amber-900/40', text: 'text-amber-700 dark:text-amber-300' },
  decision:          { Icon: FiGitBranch,   bg: 'bg-purple-50 dark:bg-purple-900/40', text: 'text-purple-600 dark:text-purple-400' },
  workflow_instance: { Icon: FiLayers,      bg: 'bg-orange-50 dark:bg-orange-900/40', text: 'text-orange-600 dark:text-orange-400' },
};

const DependencyNodeComponent: React.FC<{ data: DependencyNodeData }> = ({ data }) => {
  const navigate = useNavigate();
  const style = kindStyle[data.kind] ?? kindStyle.task;
  const { Icon } = style;

  const nodeClasses = [
    'px-4',
    'py-2',
    'shadow-md',
    'rounded-md',
    'bg-cm-bg-surface',
    'bg-cm-bg-surface',
    'border-2',
    'border-cm-border-subtle',
    'dark:border-cm-border-mid',
    'w-[200px]',
  ];

  if (data.complete) {
    nodeClasses.push('border-green-500', 'dark:border-green-400', 'opacity-60');
  }

  const content = (
    <div className={nodeClasses.join(' ')}>
      <div className="flex items-center gap-3">
        <div className="flex-shrink-0">
          <div className={`w-10 h-10 flex items-center justify-center rounded-full ${style.bg}`}>
            <Icon className={`w-6 h-6 ${style.text}`} />
          </div>
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-cm-text-primary truncate" title={data.title}>
            {data.title}
          </div>
          {data.meta && (
            <div className="text-xs text-blue-600 mt-1 truncate" title={data.meta}>
              {data.meta}
            </div>
          )}
        </div>
      </div>
      {data.linkHref && (
        <div className="mt-2 text-right">
          <button
            type="button"
            className="text-xs text-blue-600 hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              navigate(data.linkHref!);
            }}
          >
            View details
          </button>
        </div>
      )}
      {/* Handles for edge connections */}
      <Handle type="target" position={Position.Top} className="!bg-transparent !border-none" />
      <Handle type="source" position={Position.Bottom} className="!bg-transparent !border-none" />
    </div>
  );

  const tooltipContent = (
    <div className="max-w-xs">
      <div className="font-medium mb-1">{{ task: 'Task', outcome: 'Outcome', decision: 'Decision', workflow_instance: 'Workflow' }[data.kind]}</div>
      <div className="text-sm">{data.title}</div>
      {data.meta && <div className="text-xs opacity-80 mt-1">{data.meta}</div>}
      {data.linkHref && (
        <div className="mt-2">
          <button
            type="button"
            className="text-xs text-blue-600 underline"
            onClick={(e) => {
              e.stopPropagation();
              navigate(data.linkHref!);
            }}
          >
            Open detail page
          </button>
        </div>
      )}
    </div>
  );

  return (
    <Tooltip content={tooltipContent} placement="bottom" style="light">
      {/* span to ensure tooltip anchors to a single element */}
      <span className="inline-block">{content}</span>
    </Tooltip>
  );
};

const DependencyGraph: React.FC<DependencyGraphProps> = ({
  nodeIds,
  className = '',
  maxNodes = 50
}) => {
  const { database, dependencyGraph, workspaceId } = useDatabase();
  const { buildUrl } = useWorkspace();
  const [layoutedNodes, setLayoutedNodes] = useState<Node[]>([]);
  const [layoutedEdges, setLayoutedEdges] = useState<Edge[]>([]);
  const [layoutLoading, setLayoutLoading] = useState(false);

  // Function to apply ELK layout to nodes
  const getLayoutedElements = useCallback(async (nodes: Node[], edges: Edge[]) => {
    if (!nodes.length) return { nodes, edges };

    setLayoutLoading(true);

    // Convert the nodes and edges to the format ELK expects
    const elkGraph = {
      id: 'root',
      layoutOptions: elkOptions,
      children: nodes.map(node => ({
        id: node.id,
        width: 200,
        height: 100
      })),
      edges: edges.map(edge => ({
        id: edge.id,
        sources: [edge.source],
        targets: [edge.target]
      }))
    };

    try {
      // Calculate layout with ELK
      // @ts-ignore
      const layoutedGraph = await elk.layout(elkGraph);

      // Apply the calculated layout to the nodes
      const layoutedNodes = nodes.map(node => {
        const elkNode = layoutedGraph.children?.find(n => n.id === node.id);
        if (elkNode && elkNode.x !== undefined && elkNode.y !== undefined) {
          node.position = {
            x: elkNode.x,
            y: elkNode.y
          };
        }
        return node;
      });

      setLayoutLoading(false);
      return { nodes: layoutedNodes, edges };
    } catch (error) {
      console.error('Error calculating layout:', error);
      setLayoutLoading(false);
      return { nodes, edges };
    }
  }, []);

  const { nodes, edges } = useMemo(() => {
    if (!database || !dependencyGraph || !nodeIds.length) {
      return { nodes: [], edges: [] };
    }

    const nodeMap = new Map<string, Node>();
    const edgeList: Edge[] = [];

    // Create a set of node IDs to include in the graph
    const nodeIdStrings = new Set<string>();

    // Convert all provided nodeIds to strings for easier lookup
    nodeIds.forEach(nodeId => {
      nodeIdStrings.add(dependencyNodeIdToString(nodeId));
    });

    // Filter to max nodes if needed
    const filteredNodeIds = nodeIdStrings.size <= maxNodes
      ? Array.from(nodeIdStrings)
      : Array.from(nodeIdStrings).slice(0, maxNodes);

    // Get tasks, outcomes, decisions, and workflow instances from database
    const tasks = database ? database.tasks() : {};
    const outcomes = database ? database.outcomes() : {};
    const decisions = database ? database.decisions() : {};
    const workflowInstances = database ? database.workflow_instances() : {};

    // Create nodes
    let positionX = 0;
    let positionY = 0;

    filteredNodeIds.forEach((id) => {
      // Get node name based on ID type
      let nodeName = id;
      let nodeType = 'unknown';
      let complete = false;
      let linkHref: string | undefined = undefined;
      let meta: string | null = null;

      // Try to get a more meaningful name from the database
      if (id.includes('Task')) {
        nodeType = 'task';
        const taskId = id.replace(/Task\(|\)/g, '');
        const task = tasks[taskId];
        if (task) {
          try {
            const title = task.title();
            nodeName = typeof title === 'string' ? title : `Task ${taskId}`;
            complete = !!task.complete?.() || false;
            // light meta: try a simple metadata line without async calls
            const est = task.estimates?.() ?? [];
            if (est && Array.isArray(est) && est.length > 0) {
              meta = `${est.length} estimate${est.length === 1 ? '' : 's'}`;
            }
          } catch (err) {
            console.error(`Error getting title for task ${taskId}:`, err);
            nodeName = `Task ${taskId}`;
          }
          linkHref = buildUrl({ kind: 'task', taskId });
        }
      } else if (id.includes('Outcome')) {
        nodeType = 'outcome';
        const outcomeId = id.replace(/Outcome\(|\)/g, '');
        const outcome = outcomes[outcomeId];
        if (outcome) {
          try {
            const title = outcome.title();
            nodeName = typeof title === 'string' ? title : `Outcome ${outcomeId}`;
            // Treat outcomes with completion criterion met as complete if API exposes it; else leave as false
          } catch (err) {
            console.error(`Error getting title for outcome ${outcomeId}:`, err);
            nodeName = `Outcome ${outcomeId}`;
          }
          linkHref = buildUrl({ kind: 'outcome', outcomeId });
        }
      } else if (id.includes('Decision')) {
        nodeType = 'decision';
        const decisionId = id.replace(/Decision\(|\)/g, '');
        const decision = decisions[decisionId];
        if (decision) {
          try {
            const title = decision.title();
            nodeName = typeof title === 'string' ? title : `Decision ${decisionId}`;
            const choices = decision.choices?.() ?? [];
            if (choices.length > 0) {
              meta = `${choices.length} choice${choices.length === 1 ? '' : 's'}`;
            }
          } catch (err) {
            console.error(`Error getting title for decision ${decisionId}:`, err);
            nodeName = `Decision ${decisionId}`;
          }
          linkHref = buildUrl({ kind: 'decision', decisionId });
        }
      } else if (id.includes('WorkflowInstance')) {
        nodeType = 'workflow_instance';
        const instanceId = id.replace(/WorkflowInstance\(|\)/g, '');
        const instance = workflowInstances[instanceId];
        if (instance) {
          try {
            nodeName = `Workflow: ${instance.template_id()}`;
          } catch (err) {
            console.error(`Error getting info for workflow instance ${instanceId}:`, err);
            nodeName = `Workflow ${instanceId}`;
          }
          linkHref = buildUrl({ kind: 'workflow-instance', instanceId });
        }
      }

      // Set initial position (will be overridden by layout)
      positionX = Math.random() * 500;
      positionY = Math.random() * 500;

      const node: Node<DependencyNodeData> = {
        id,
        type: 'dependencyNode',
        data: {
          id,
          kind: nodeType as DependencyNodeData['kind'],
          title: nodeName,
          complete,
          meta,
          linkHref,
        },
        position: { x: positionX, y: positionY },
        draggable: true,
      };

      nodeMap.set(id, node);
    });

    // Create edges between nodes in our filtered set
    let edgeIndex = 0;

    // For each node in our set, find its dependencies and dependents
    nodeIds.forEach(nodeId => {
      const targetIdStr = dependencyNodeIdToString(nodeId);

      // Add edges for dependencies (outgoing)
      if (dependencyGraph) {
        const dependencies = dependencyGraph.get_dependencies(nodeId);
        dependencies.forEach((sourceNodeId: DependencyNodeId) => {
          const sourceIdStr = dependencyNodeIdToString(sourceNodeId);

          // Only create edge if both nodes are in our filtered set
          if (nodeMap.has(sourceIdStr) && nodeMap.has(targetIdStr)) {
            const isDarkMode = document.documentElement.classList.contains('dark');
            const edgeColor = isDarkMode ? '#94a3b8' : '#64748b';

            edgeList.push({
              id: `edge-${edgeIndex}`,
              source: sourceIdStr,
              target: targetIdStr,
              animated: true,
              style: { stroke: edgeColor, strokeWidth: 2 },
              type: 'smoothstep',
              markerEnd: {
                type: MarkerType.Arrow,
                color: edgeColor,
                width: 20,
                height: 20
              }
            });
            edgeIndex++;
          }
        });
      }
    });

    const nodesArray = Array.from(nodeMap.values());
    return { nodes: nodesArray, edges: edgeList };
  }, [database, dependencyGraph, nodeIds, maxNodes]);

  // Stable fingerprint of graph topology — only IDs and connections, not data (titles, etc.)
  const topologyKey = useMemo(() => {
    const nk = nodes.map(n => n.id).sort().join(',');
    const ek = edges.map(e => `${e.source}->${e.target}`).sort().join(',');
    return `${nk}|${ek}`;
  }, [nodes, edges]);

  const prevTopologyKey = useRef(topologyKey);

  // Re-layout only when topology changes (node added/removed, edge added/removed)
  useEffect(() => {
    const topologyChanged = prevTopologyKey.current !== topologyKey;
    prevTopologyKey.current = topologyKey;

    if (nodes.length > 0 && topologyChanged) {
      getLayoutedElements(nodes, edges).then(({ nodes: ln, edges: le }) => {
        setLayoutedNodes(ln);
        setLayoutedEdges(le);
      });
    } else if (nodes.length > 0 && layoutedNodes.length > 0) {
      // Topology unchanged — merge fresh data into existing positioned nodes
      const posMap = new Map(layoutedNodes.map(n => [n.id, n.position]));
      setLayoutedNodes(nodes.map(n => ({ ...n, position: posMap.get(n.id) ?? n.position })));
      setLayoutedEdges(edges);
    }
  }, [topologyKey, nodes, edges, getLayoutedElements]);

  if (layoutLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className={`h-full w-full ${className}`}>
      <ReactFlow
        nodes={layoutedNodes.length > 0 ? layoutedNodes : nodes}
        edges={layoutedEdges.length > 0 ? layoutedEdges : edges}
        fitView
        defaultEdgeOptions={{ type: 'smoothstep' }}
        proOptions={{ hideAttribution: true }}
        zoomOnScroll={true}
        panOnScroll={true}
        nodesDraggable={true}
        nodeTypes={{ dependencyNode: DependencyNodeComponent } as NodeTypes}
      >
        <Background />
        <Panel position="top-right">
          <div className="bg-cm-bg-surface p-2 rounded shadow-md text-sm">
            <div className="flex items-center mb-1">
              <div className="w-4 h-4 bg-blue-50 dark:bg-blue-900/40 border border-cm-border-mid rounded mr-2"></div>
              <span>Task</span>
            </div>
            <div className="flex items-center mb-1">
              <div className="w-4 h-4 bg-amber-50 dark:bg-amber-900/40 border border-cm-border-mid rounded mr-2"></div>
              <span>Outcome</span>
            </div>
            <div className="flex items-center mb-1">
              <div className="w-4 h-4 bg-purple-50 dark:bg-purple-900/40 border border-cm-border-mid rounded mr-2"></div>
              <span>Decision</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-orange-50 dark:bg-orange-900/40 border border-cm-border-mid rounded mr-2"></div>
              <span>Workflow</span>
            </div>
          </div>
        </Panel>
      </ReactFlow>
    </div>
  );
};

export default DependencyGraph;
